import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const STRINGS = {
  en: {
    nav: {
      home: "Home",
      about: "About",
      services: "Services",
      ai: "AI Assistant",
      practice: "Practice",
      team: "Team",
      blog: "Blog",
      resources: "Resources",
      insights: "Insights",
      laws: "Laws",
      research: "Research",
      doctrine: "Doctrine",
      judgments: "Judgments",
      contact: "Contact",
      contracts: "AI Contracts",
      consultant: "Ask Sami",
      documentReview: "Document Review",
      caseAssessment: "Case Assessment",
      aiServices: "AI Services",
      portal: "Member Portal",
      bookConsult: "Book a Consultation",
      whatsapp: "WhatsApp",
      login: "Sign in",
      register: "Register",
      logout: "Sign out",
      search: "Unified Search",
      saved: "Saved",
    },
    home: {
      eyebrow: "Aldakheel Legal Group",
      heroLine1: "Considered counsel.",
      heroLine2: "Decisive outcomes.",
      heroLead:
        "A Kuwait-based law firm advising founders, boards, and families on the matters that define their next chapter — grounded in Kuwaiti statutes, GCC commercial practice, and a thirty-year court tradition.",
      cta: "Book a consultation",
      learnMore: "Read our story",
      trustedBy: "Trusted by",
      practiceTitle: "Practice areas",
      practiceLead:
        "Six disciplines, one partnership. We assemble bespoke teams around the matter, not the org chart.",
      teamTitle: "Partners & counsel",
      insightsTitle: "Insights",
      ctaBlock: {
        eyebrow: "Begin",
        title: "A 30-minute consultation, on the record.",
        sub: "Tell us about the matter. We will tell you, candidly, whether we are the right firm.",
        button: "Schedule now",
      },
    },
    about: {
      eyebrow: "About",
      title: "A Kuwaiti firm, built around the matter.",
      lead:
        "Aldakheel Legal Group is a Kuwait-licensed advocacy and consultancy practice. We act for clients before all degrees of the Kuwaiti judiciary — Court of First Instance, Court of Appeal, and Court of Cassation — and counsel boards, founders, and families on the corporate, real-estate, and family matters that shape their futures.",
      missionTitle: "Mission",
      missionBody:
        "To deliver Kuwaiti legal counsel that is candid, commercially literate, and durable — anchored in the State's statutes and the settled jurisprudence of its courts.",
      valuesTitle: "Values",
      values: [
        { title: "Discretion", body: "We do our most important work in private." },
        { title: "Rigor", body: "Every position is stress-tested against statute and Cassation jurisprudence before it leaves the firm." },
        { title: "Partnership", body: "Senior lawyers are on every matter, every day." },
        { title: "Clarity", body: "We translate Kuwaiti law into decisions you can make." },
      ],
    },
    contact: {
      eyebrow: "Contact",
      title: "Reach the firm.",
      lead: "We respond to inbound enquiries within one business day.",
      name: "Full name",
      email: "Email",
      phone: "Phone",
      subject: "Subject",
      message: "How can we help?",
      submit: "Send message",
      success: "Thank you. A partner will be in touch shortly.",
      error: "Something went wrong. Please try again.",
      addresses: "Offices",
    },
    booking: {
      eyebrow: "Consultation",
      title: "Schedule a consultation.",
      lead:
        "Choose a date, share a brief, and a partner from the relevant practice will confirm within one business day.",
      name: "Full name",
      email: "Email",
      phone: "Phone",
      practice: "Area of law",
      date: "Preferred date",
      time: "Preferred time",
      notes: "Brief description (optional)",
      submit: "Request consultation",
      success: "Request received. A partner will confirm shortly.",
      pickDate: "Pick a date",
    },
    auth: {
      loginTitle: "Member sign in",
      loginLead: "Access your matters and documents.",
      registerTitle: "Create your member account",
      registerLead: "Set up secure access to your portal.",
      name: "Full name",
      email: "Email",
      phone: "Phone (optional)",
      password: "Password",
      submitLogin: "Sign in",
      submitRegister: "Create account",
      noAccount: "No account?",
      haveAccount: "Already have an account?",
      register: "Register",
      login: "Sign in",
      forgotPwd: "Forgot password?",
    },
    portal: {
      title: "Your matters",
      lead: "An overview of active engagements with the firm.",
      cases: "Active matters",
      documents: "Documents",
      status: "Status",
      lastUpdate: "Last update",
      empty: "You have no matters yet. A partner will activate your portal once we onboard.",
    },
    blog: {
      eyebrow: "Insights",
      title: "Considered writing on the matters of the day.",
      readMore: "Read",
      minutes: "min read",
    },
    footer: {
      tagline: "Advocacy. Integrity. Results.",
      offices: "Offices",
      contact: "Contact",
      navigate: "Navigate",
      legal: "© 2026 Aldakheel Legal Group. All rights reserved.",
      privacy: "Privacy",
      terms: "Terms",
    },
  },
  ar: {
    nav: {
      home: "الرئيسية",
      about: "من نحن",
      services: "خدماتنا",
      ai: "الذكاء القانوني",
      practice: "ممارساتنا",
      team: "الفريق",
      blog: "المدونة",
      resources: "الموارد",
      insights: "رؤى",
      laws: "القوانين",
      research: "الأبحاث",
      doctrine: "المراجع الفقهية",
      judgments: "الأحكام",
      contact: "تواصل",
      contracts: "صياغة العقود",
      consultant: "اسأل سامي",
      documentReview: "مراجعة المستندات",
      caseAssessment: "تقييم قضيتك",
      aiServices: "خدمات الذكاء",
      portal: "بوابة العميل",
      bookConsult: "احجز استشارة",
      whatsapp: "واتساب",
      login: "تسجيل الدخول",
      register: "إنشاء حساب",
      logout: "تسجيل الخروج",
      search: "البحث الموحّد",
      saved: "محفوظاتي",
    },
    home: {
      eyebrow: "مجموعة الدخيل القانونية",
      heroLine1: "استشارة مدروسة.",
      heroLine2: "نتائج حاسمة.",
      heroLead:
        "مكتب محاماة كويتي يقدّم المشورة للمؤسسين ومجالس الإدارة والعائلات في المسائل التي تشكّل فصلهم القادم — بأساس راسخ في القوانين الكويتية والممارسة التجارية الخليجية وتقاليد المحاكم الممتدة لعقود.",
      cta: "احجز استشارة",
      learnMore: "اقرأ قصتنا",
      trustedBy: "موثوقون من",
      practiceTitle: "مجالات الممارسة",
      practiceLead:
        "ست تخصصات، شراكة واحدة. نشكل فرقًا مخصصة حول كل قضية.",
      teamTitle: "فريقنا",
      insightsTitle: "رؤى",
      ctaBlock: {
        eyebrow: "ابدأ",
        title: "استشارة لمدة 30 دقيقة، رسمية.",
        sub: "أخبرنا عن الأمر. سنخبرك بصراحة ما إذا كنا الشركة المناسبة.",
        button: "احجز الآن",
      },
    },
    about: {
      eyebrow: "من نحن",
      title: "مكتب كويتي، مبنيٌ حول القضية.",
      lead:
        "مجموعة الدخيل القانونية مكتب محاماة واستشارات قانونية مرخّص في دولة الكويت. نحضر عن عملائنا أمام درجات القضاء الكويتي كافة — أول درجة والاستئناف والتمييز — ونقدّم المشورة لمجالس الإدارة والمؤسسين والعائلات في المسائل المؤسسية والعقارية والأسرية التي تصوغ مستقبلهم.",
      missionTitle: "المهمة",
      missionBody:
        "تقديم استشارة قانونية كويتية صريحة تراعي الواقع التجاري ودائمة — راسخة في نصوص القانون واجتهاد محكمة التمييز المستقر.",
      valuesTitle: "القيم",
      values: [
        { title: "التحفظ", body: "نقوم بأهم أعمالنا في الخفاء." },
        { title: "الصرامة", body: "كل موقف يخضع لاختبار الضغط في ضوء النص والاجتهاد قبل أن يغادر المكتب." },
        { title: "الشراكة", body: "كبار المحامين على كل قضية، كل يوم." },
        { title: "الوضوح", body: "نترجم القانون الكويتي إلى قرارات يمكنك اتخاذها." },
      ],
    },
    contact: {
      eyebrow: "اتصل",
      title: "تواصل مع الشركة.",
      lead: "نرد على الاستفسارات في غضون يوم عمل واحد.",
      name: "الاسم الكامل",
      email: "البريد الإلكتروني",
      phone: "الهاتف",
      subject: "الموضوع",
      message: "كيف يمكننا المساعدة؟",
      submit: "إرسال الرسالة",
      success: "شكرًا لك. سيتواصل معك أحد الشركاء قريبًا.",
      error: "حدث خطأ. يرجى المحاولة مرة أخرى.",
      addresses: "المكاتب",
    },
    booking: {
      eyebrow: "استشارة",
      title: "حدد موعد استشارة.",
      lead:
        "اختر تاريخًا، وشارك ملخصًا، وسيؤكد شريك من الممارسة المعنية خلال يوم عمل.",
      name: "الاسم الكامل",
      email: "البريد الإلكتروني",
      phone: "الهاتف",
      practice: "مجال القانون",
      date: "التاريخ المفضل",
      time: "الوقت المفضل",
      notes: "وصف موجز (اختياري)",
      submit: "طلب استشارة",
      success: "تم استلام الطلب. سيتم التأكيد قريبًا.",
      pickDate: "اختر تاريخًا",
    },
    auth: {
      loginTitle: "تسجيل دخول العضو",
      loginLead: "ادخل إلى قضاياك ومستنداتك.",
      registerTitle: "إنشاء حساب العضو",
      registerLead: "قم بإعداد وصول آمن إلى البوابة.",
      name: "الاسم الكامل",
      email: "البريد الإلكتروني",
      phone: "الهاتف (اختياري)",
      password: "كلمة المرور",
      submitLogin: "تسجيل الدخول",
      submitRegister: "إنشاء حساب",
      noAccount: "ليس لديك حساب؟",
      haveAccount: "لديك حساب بالفعل؟",
      register: "تسجيل",
      login: "تسجيل الدخول",
      forgotPwd: "نسيت كلمة المرور؟",
    },
    portal: {
      title: "قضاياك",
      lead: "نظرة عامة على الارتباطات النشطة مع الشركة.",
      cases: "القضايا النشطة",
      documents: "المستندات",
      status: "الحالة",
      lastUpdate: "آخر تحديث",
      empty: "لا توجد قضايا حتى الآن. سيقوم أحد الشركاء بتفعيل بوابتك بمجرد الانضمام.",
    },
    blog: {
      eyebrow: "رؤى",
      title: "كتابات مدروسة عن قضايا اليوم.",
      readMore: "اقرأ",
      minutes: "د قراءة",
    },
    footer: {
      tagline: "المرافعة. النزاهة. النتائج.",
      offices: "المكاتب",
      contact: "اتصل",
      navigate: "تصفح",
      legal: "© 2026 مجموعة الدخيل القانونية. جميع الحقوق محفوظة.",
      privacy: "الخصوصية",
      terms: "الشروط",
    },
  },
};

const I18nCtx = createContext({ lang: "en", t: STRINGS.en, setLang: () => {} });

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("alg_lang") || "ar");

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    localStorage.setItem("alg_lang", lang);
  }, [lang]);

  const value = useMemo(() => ({ lang, t: STRINGS[lang], setLang }), [lang]);
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
};

export const useI18n = () => useContext(I18nCtx);
